import { useState } from 'react'
import type { CreatorShopApplicationDetail } from '../../services/adminApi'
import { CustomButton } from '../../components/common/CustomButton'
import { CustomTextInput } from '../../components/common/CustomTextInput'
import { usePermission } from '../../hooks/usePermission'

interface ApplicationDetailModalProps {
  application: CreatorShopApplicationDetail
  onClose: () => void
  onApprove: (decisionNotes?: string) => Promise<void>
  onReject: (category: string, decisionNotes?: string) => Promise<void>
  onWaitlist: (target: 'waitlisted' | 'pending') => Promise<void>
}

type Action = 'approve' | 'reject' | null

const REJECTION_CATEGORIES = [
  { value: 'not_enough_work', label: 'Not enough work to evaluate' },
  { value: 'wrong_verticals', label: "Doesn't fit our verticals" },
  { value: 'portfolio_cooldown', label: 'Re-apply after building portfolio' },
  { value: 'duplicate_application', label: 'Duplicate application' },
  { value: 'other', label: 'Other (use notes)' },
]

export function ApplicationDetailModal({
  application,
  onClose,
  onApprove,
  onReject,
  onWaitlist,
}: ApplicationDetailModalProps) {
  const [action, setAction] = useState<Action>(null)
  const [notes, setNotes] = useState('')
  const [rejectionCategory, setRejectionCategory] = useState(REJECTION_CATEGORIES[0].value)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const canEdit = usePermission('creators', 'edit')
  const canDecide = canEdit && application.status === 'pending'
  const canMoveWaitlist =
    canEdit &&
    (application.status === 'pending' || application.status === 'waitlisted')

  const handleApprove = async () => {
    setLoading(true)
    setError(null)
    try {
      await onApprove(notes.trim() || undefined)
      setAction(null)
      setNotes('')
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to approve')
    } finally {
      setLoading(false)
    }
  }

  const handleReject = async () => {
    setLoading(true)
    setError(null)
    try {
      await onReject(rejectionCategory, notes.trim() || undefined)
      setAction(null)
      setNotes('')
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to reject')
    } finally {
      setLoading(false)
    }
  }

  const handleWaitlistToggle = async () => {
    setLoading(true)
    setError(null)
    try {
      const target = application.status === 'waitlisted' ? 'pending' : 'waitlisted'
      await onWaitlist(target)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to update waitlist')
    } finally {
      setLoading(false)
    }
  }

  const submittedAt = application.submitted_at
    ? new Date(application.submitted_at).toLocaleString()
    : '—'
  const decidedAt = application.decided_at
    ? new Date(application.decided_at).toLocaleString()
    : null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div
        className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white dark:bg-[#1a1520] p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-6">
          <div>
            <h2 className="font-heading text-xl font-bold text-gray-900 dark:text-gray-100">
              Creator Shop Application
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 capitalize">
              {application.status}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300">
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="mb-6 grid grid-cols-2 gap-4 text-sm">
          <Field label="Shop name" value={application.shop_name} />
          <Field label="Desired handle" value={application.desired_handle} mono />
          <Field label="Applicant" value={application.applicant_email} />
          <Field label="Applicant ID" value={application.applicant_user_id} mono small />
          <Field label="Submitted" value={submittedAt} />
          {decidedAt && <Field label="Decided" value={decidedAt} />}
          {application.decided_by && (
            <Field label="Decided by" value={application.decided_by} mono small />
          )}
          {application.decision_reason_category && (
            <Field label="Reason category" value={application.decision_reason_category} mono />
          )}
        </div>

        <Section title="What they make" body={application.what_they_make} />
        <Section title="Application reason" body={application.application_reason} />
        <Section title="Listing focus" body={application.listing_focus} />
        <Section title="Upcoming events" body={application.upcoming_events} />

        {application.expected_categories && application.expected_categories.length > 0 && (
          <div className="mb-4">
            <p className="mb-1 text-sm font-medium text-gray-700 dark:text-gray-200">
              Expected categories
            </p>
            <div className="flex flex-wrap gap-1.5">
              {application.expected_categories.map((c) => (
                <span
                  key={c}
                  className="rounded-full bg-[#f9f4ff] dark:bg-[#3d2c4f] px-2.5 py-1 text-xs text-[#503864] dark:text-[#d0b8df]"
                >
                  {c}
                </span>
              ))}
            </div>
          </div>
        )}

        {application.work_links && application.work_links.length > 0 && (
          <div className="mb-4">
            <p className="mb-1 text-sm font-medium text-gray-700 dark:text-gray-200">Work links</p>
            <ul className="space-y-1">
              {application.work_links.map((link, idx) => {
                const url = typeof link.url === 'string' ? link.url : ''
                const label = typeof link.label === 'string' && link.label ? link.label : url
                return (
                  <li key={`${url}-${idx}`} className="text-sm">
                    <a
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#7f4f9a] dark:text-[#b58fcc] hover:underline break-all"
                    >
                      {label}
                    </a>
                  </li>
                )
              })}
            </ul>
          </div>
        )}

        {application.decision_notes && (
          <div className="mb-6 rounded-xl bg-gray-50 dark:bg-[#2a1f35] p-4">
            <p className="text-sm font-medium text-gray-700 dark:text-gray-200">Admin notes</p>
            <p className="text-sm text-gray-600 dark:text-gray-300 whitespace-pre-wrap">
              {application.decision_notes}
            </p>
          </div>
        )}

        {error && (
          <div className="mb-4 rounded-xl bg-red-50 dark:bg-red-900/30 p-3 text-sm text-red-700 dark:text-red-300">
            {error}
          </div>
        )}

        {action === 'approve' && (
          <div className="mb-6 rounded-xl border border-gray-200 dark:border-[#3d2c4f] p-4">
            <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-3">Approve application</h4>
            <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">
              Creates the Creator shop, grants paid tier for 365 days, awards the founding
              badge, and notifies the applicant.
            </p>
            <CustomTextInput
              label="Internal notes (optional)"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Why we're approving this one"
            />
            <div className="mt-4 flex gap-2">
              <CustomButton onClick={handleApprove} disabled={loading} variant="success">
                {loading ? 'Approving...' : 'Confirm approve'}
              </CustomButton>
              <CustomButton onClick={() => setAction(null)} variant="secondary">
                Cancel
              </CustomButton>
            </div>
          </div>
        )}

        {action === 'reject' && (
          <div className="mb-6 rounded-xl border border-gray-200 dark:border-[#3d2c4f] p-4">
            <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-3">Reject application</h4>
            <div className="mb-3">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Reason category (drives applicant-facing email)
              </label>
              <select
                value={rejectionCategory}
                onChange={(e) => setRejectionCategory(e.target.value)}
                className="mt-1 w-full rounded-xl border border-gray-300 dark:border-[#3d2c4f] bg-white dark:bg-[#1a1520] text-gray-900 dark:text-gray-100 px-4 py-2.5 text-sm focus:border-[#503864] focus:outline-none focus:ring-1 focus:ring-[#503864]"
              >
                {REJECTION_CATEGORIES.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <CustomTextInput
              label="Internal notes (optional)"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Context for the team — not shown to the applicant"
            />
            <div className="mt-4 flex gap-2">
              <CustomButton onClick={handleReject} disabled={loading} variant="danger">
                {loading ? 'Rejecting...' : 'Confirm reject'}
              </CustomButton>
              <CustomButton onClick={() => setAction(null)} variant="secondary">
                Cancel
              </CustomButton>
            </div>
          </div>
        )}

        {!action && (
          <div className="flex flex-wrap gap-2">
            {canDecide && (
              <>
                <CustomButton onClick={() => setAction('approve')} variant="success">
                  Approve
                </CustomButton>
                <CustomButton onClick={() => setAction('reject')} variant="danger">
                  Reject
                </CustomButton>
              </>
            )}
            {canMoveWaitlist && (
              <CustomButton
                onClick={handleWaitlistToggle}
                disabled={loading}
                variant="secondary"
              >
                {application.status === 'waitlisted'
                  ? 'Move to pending'
                  : 'Move to waitlist'}
              </CustomButton>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

function Field({
  label,
  value,
  mono,
  small,
}: {
  label: string
  value: string | null | undefined
  mono?: boolean
  small?: boolean
}) {
  return (
    <div>
      <span className="text-gray-500 dark:text-gray-400">{label}:</span>
      <span
        className={`ml-2 ${mono ? 'font-mono' : ''} ${small ? 'text-xs' : ''} text-gray-900 dark:text-gray-100`}
      >
        {value || '—'}
      </span>
    </div>
  )
}

function Section({ title, body }: { title: string; body: string | null | undefined }) {
  if (!body) return null
  return (
    <div className="mb-4">
      <p className="mb-1 text-sm font-medium text-gray-700 dark:text-gray-200">{title}</p>
      <p className="text-sm text-gray-600 dark:text-gray-300 whitespace-pre-wrap">{body}</p>
    </div>
  )
}
